import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Search, Tag, X } from "lucide-react";
import "./DrawerTagFilter.scss";

const tagColors = ["#3b82f6", "#a855f7", "#22c55e", "#ef4444", "#ec4899", "#f97316", "#14b8a6", "#06b6d4"];

export default function DrawerTagFilter({ options = [], selectedId, selectedName, onSelect, onClear }) {
  const [isOpen, setIsOpen] = useState(false);
  const [alignRight, setAlignRight] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef(null);
  const searchRef = useRef(null);
  const safeOptions = useMemo(() => Array.isArray(options) ? options : [], [options]);
  const filtered = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase();
    return safeOptions
      .filter((option) => option?.name?.toLocaleLowerCase().includes(normalized))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [safeOptions, query]);

  useEffect(() => {
    if (!isOpen) return undefined;
    searchRef.current?.focus();
    const closeOnOutside = (event) => {
      if (!containerRef.current?.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener("pointerdown", closeOnOutside);
    return () => document.removeEventListener("pointerdown", closeOnOutside);
  }, [isOpen]);

  const selectTag = (option) => {
    onSelect(option);
    setQuery("");
    setIsOpen(false);
  };

  const toggleMenu = () => {
    if (!isOpen) {
      const rect = containerRef.current?.getBoundingClientRect();
      const panelWidth = Math.min(320, window.innerWidth - 32);
      setAlignRight(Boolean(rect && rect.left + panelWidth > window.innerWidth - 16));
    }
    setIsOpen((open) => !open);
  };

  return (
    <div className={`drawer-tag-filter ${isOpen ? "is-open" : ""} ${alignRight ? "is-right-aligned" : ""}`} ref={containerRef} onClick={(event) => event.stopPropagation()} onKeyDown={(event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        setIsOpen(false);
      }
    }}>
      <div className="drawer-tag-filter__control">
        <button type="button" className="drawer-tag-filter__trigger" aria-expanded={isOpen} aria-haspopup="listbox" onClick={toggleMenu}>
          <Tag size={16} aria-hidden="true" />
          <span>{selectedName || "Tags"}</span>
          <ChevronDown size={15} className="drawer-tag-filter__chevron" aria-hidden="true" />
        </button>
        {selectedId && <button type="button" className="drawer-tag-filter__clear" onClick={() => { onClear(); setQuery(""); }} aria-label="Clear tag filter"><X size={14} /></button>}
      </div>

      {isOpen && (
        <div className="drawer-tag-filter__panel">
          <div className="drawer-tag-filter__heading"><strong>Filter by tag</strong><span>{safeOptions.length} tags</span></div>
          <label className="drawer-tag-filter__search">
            <Search size={16} aria-hidden="true" />
            <input ref={searchRef} type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search tags" aria-label="Search tags" />
          </label>
          <div className="drawer-tag-filter__list" role="listbox" aria-label="Tags">
            {filtered.length ? filtered.map((option, index) => (
              <button type="button" role="option" aria-selected={selectedId === option._id} key={option._id} className={`drawer-tag-filter__option ${selectedId === option._id ? "is-selected" : ""}`} onClick={() => selectTag(option)}>
                <span className="drawer-tag-filter__dot" style={{ backgroundColor: tagColors[index % tagColors.length] }} />
                <span className="drawer-tag-filter__name">{option.name}</span>
                {selectedId === option._id && <Check size={16} aria-hidden="true" />}
              </button>
            )) : <p className="drawer-tag-filter__empty">No matching tags</p>}
          </div>
        </div>
      )}
    </div>
  );
}
